import ViewViewModel from '@/ViewModels/ViewViewModel';
import DetailViewContextT from '@/ViewModels/Contexts/DetailViewContext';
import RADApplication from '@/Management/RADApplication';
import MessageBoxViewModel from '@/ViewModels/Controls/MessageBoxViewModel';
import Config from '@/Management/GlobalConfiguration';
import NavigationService from '@/Management/NavigationService';
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import NavigationHelper from '@/Management/NavigationHelper';
import Messenger from '@/Utility/Messenger';
import LabelViewModelBuilder from '@/Builder/LabelViewModelBuilder';
import type BreadCrumbViewModel from '@/ViewModels/Controls/BreadCrumbViewModel';
import ComponentManager from '@/Management/ComponentManager';
import NavigationStartedMessage from '@/Messages/NavigationStartedMessage';
import type DetailViewViewFactory from '@/Factories/ViewFactories/DetailViewViewFactory';
import type ModalDetailViewWindowViewModel from '@/Components/ModalDetailView/ModalDetailViewWindowViewModel';
import type ModalDetailViewWindow from '@/Components/ModalDetailView/ModalDetailViewWindow';
import Utilities from '@/Utility/Utilities';
import Loader from '@/Utility/Loader';
import AssociationNavigationMessage from '@/Messages/AssociationNavigationMessage';

/**
 * Repräsentiert das ViewModel einer DetailView
 */
export default class DetailViewViewModel extends ViewViewModel {

    /**
     * @override
     */
    public viewFactory: DetailViewViewFactory;

    public detailViewContextStack: KnockoutObservableArray<DetailViewContextT>;

    private _detailViewContextCounter: number;

    /**
     * Beinhaltet den aktuellen DetailViewContext
     */
    public currentContext: KnockoutComputed<DetailViewContextT>;

    private isCurrentContextBusy: KnockoutObservable<boolean>;
    private currentContextBusySubscription: KnockoutSubscription = null;

    private ShowSavingDialogOnStackNavigation = RADApplication.getInstance().isSystemParamSetAndTrue('Gui.ShowSavingDialogOnStackNavigation');

    private DetailViewContext = ComponentManager.getViewModel<typeof DetailViewContextT>('DetailViewContext');

    public pageTitle: BreadCrumbViewModel;

    protected serializedFilters: any;

    /**
     * Ein Item, zu welchem der Benutzer beim Wiederherstellen der Ansicht gescrollt werden soll.
     */
    private viewAnchor: string;

    constructor(protected params: any) {
        super(params);
        this.serializedFilters = this.paramOrDefault(params.serializedFilters, null);
        this.detailViewContextStack = this.observableOrDefault([]) as KnockoutObservableArray<DetailViewContextT>;
        this.currentContext = this.defineComputed(() => {
            let result: DetailViewContextT = null;
            const stack = this.detailViewContextStack();

            if (stack.length > 0) {
                result = stack[stack.length - 1];
            }

            RADApplication.getInstance().currentDetailViewContext(result);

            return result;
        });
        this.trackCurrentContextBusyState();

        this._detailViewContextCounter = 0;
        this.createPageTitle();

        this.initContext();
        this.registerForNavigationStartedMessage();
        this.viewAnchor = null;
    }

    protected initContext(): void {
        const type = this.viewFactory.pdTypeName;
        const pdoId = this.viewFactory.pdoId;

        const contextPdoId = this.viewFactory.contextObject;
        const contextTypeName = this.viewFactory.contextTypeName;
        const contextRelation = this.viewFactory.contextRelation;

        const contextParameters = this.viewFactory.contextParameters;
        const callId = this.viewFactory.contextID;

        // Ersten DetailViewContext mit der an die Factory übergebenen Daten eröffnen
        this.createContext(null, type, pdoId, contextPdoId, contextTypeName, contextRelation, contextParameters, callId);
    }

    private trackCurrentContextBusyState() {
        this.isCurrentContextBusy = this.observableOrDefault(false);

        this.defineSubscription(this.currentContext, currentContext => {
            if (this.currentContextBusySubscription) {
                // Es muss immer nur der Busy-State des aktuellen DVCs getrackt werden.
                // Deshalb wird die Subscription vom vorherigen DVC hier abgeräumt.
                this.currentContextBusySubscription.dispose();
                this.currentContextBusySubscription = null;
            }

            if (this.isDisposed)
                return;

            this.isCurrentContextBusy(false);
            if (!currentContext)
                return;

            this.currentContextBusySubscription = currentContext.isBusy.subscribe(busy => {
                if (this.isDisposed)
                    return;

                this.isCurrentContextBusy(busy);
            });

            // Zur Sicherheit fügen wir die aktuelle Subscription noch in die SubscriptionCollection ein,
            // damit diese beim normalen Disposing mit abgeräumt wird
            this.subscriptionCollection.push(this.currentContextBusySubscription);

            // Initiale Auswertung des Busy-States
            this.isCurrentContextBusy(currentContext.isBusy());
        })(this.currentContext());

        this.defineSubscription(this.isCurrentContextBusy, busy => {
            if (this.isDisposed)
                return;

            if (busy)
                this.increaseBusyCounter();
            else
                this.decreaseBusyCounter();
        });

        // Initiale Auswertung (nur wenn busy, sonst könnte der Counter negativ werden)
        if (this.isCurrentContextBusy()) {
            this.increaseBusyCounter();
        }
    }

    /**
     * Indicates whether the principal is displayed in the breadcrumb if the object is in a foreign principal
     * @param ergName
     * @param principalName
     * @param pdoId
     */
    public showForeignPrincipalPostfix(ergName: string, principalName: string, pdoId: string): boolean {
        return true;
    }

    /**
     * Zeigt - falls notwendig - ein Fenster zum Bestätigen und Speichern der Änderungen
     * @param callback
    */
    public confirmCreateContext(callback: (success: boolean) => void, showDialog: boolean = true): void {
        const currentContext = this.currentContext();

        if (currentContext == null) {
            callback(null);
        } else {
            // Inline-Contexte können ignoriert werden (2. Parameter auf false), da diese beim Verlassen noch einmal explizit zum Server geschickt werden
            const isModified = currentContext.hasPendingModifications(true, false) === true && !currentContext.genericObject.isHistorized;
            const confirm = isModified && this.ShowSavingDialogOnStackNavigation && showDialog && !currentContext.useShortTransactions();

            const showSavingInformation = (dialogCallback) => {
                const yesCallback = () => { dialogCallback(true); };
                const noCallback = () => { dialogCallback(false); };
                const messageBoxViewModel = MessageBoxViewModel.createSaveWarningMessageBox(yesCallback, noCallback);

                RADApplication.getInstance().showMessageBox(messageBoxViewModel);
            };

            if (!Config.detailView.AlwaysCloseDetailViewContext) {
                //Bisheriges Verhalten: Der Stack bleibt erhalten
                if (confirm) {
                    // Kontext hat ungespeicherte Änderungen
                    showSavingInformation((dialogResult) => {
                        if (!dialogResult) return;

                        $.when(currentContext.apply()).then((applySuccess) => {
                            if (applySuccess !== false) {
                                callback(true);
                            }
                        });
                    });
                } else {
                    // Änderungen übertragen bevor eine neue Dv geöffnet wird.
                    $.when(currentContext.updateProperties()).then((updatePropertiesSuccess) => {
                        if (updatePropertiesSuccess !== false) {
                            callback(true);
                        }
                    });
                }
            }
            else { // Neues Verhalten: Jeder Context wird geschlossen
                // Kontext hat ungespeicherte Änderungen
                if (confirm) {
                    showSavingInformation((dialogResult) => {
                        if (!dialogResult)
                            return;

                        currentContext.setPendingDVC(true);
                        // Änderungen übertragen bevor eine neue DV geöffnet wird.
                        $.when(currentContext.saveBeforeStackNavigation()).then((saveSuccess) => {
                            if (saveSuccess) {
                                callback(true);
                            } else {
                                // Falls nach einem fehlgeschlagegenen Speichern auf Abbrechen geklickt wird, muss _hasPendingDVC wieder auf false gesetzt werden.
                                // sonst wurde eine leere Seite angezeigt.
                                currentContext.setPendingDVC(false);
                            }
                        });
                    });
                }
                else {
                    // Wenn keine Änderungen muss nicht der Dialog zum Speichern geöffnet werden, sondern es kann direkt wegnavigiert werden.
                    if (!isModified && !currentContext.useShortTransactions()) {
                        $.when(callback(true)).then(() => {
                            currentContext.dispose();
                        });
                    }
                    else {
                        $.when(currentContext.saveBeforeStackNavigation()).then((saveSuccess) => {
                            if (saveSuccess) {
                                callback(true);
                            } else {
                                // Falls nach einem fehlgeschlagegenen Speichern auf Abbrechen geklickt wird, muss _hasPendingDVC wieder auf false gesetzt werden.
                                // sonst wurde eine leere Seite angezeigt.
                                currentContext.setPendingDVC(false);
                            }
                        });
                    }
                }
            }
        }
    }

    /**
     * Erstellt einen neuen DetailViewContext und fügt ihn dem DetailViewContextStack hinzu
     * @param caller
     * @param pdTypeName
     * @param pdoId
     * @param contextPdoId
     * @param contextPdTypeName
     * @param contextRelationName
     * @param contextParameters
     * @param callId
     * @param principalFilterMode
     * @param filter
     */
    public createContext(caller: any, pdTypeName: string, pdoId: string | number, contextPdoId?: string | number, contextPdTypeName?: string, contextRelationName?: string, contextParameters?: any,
        callId?: string, principalFilterMode?: string, filter?: string) {

        let safePdoId = Utilities.handlePdoId(pdoId);
        let safeContextPdoId = Utilities.handlePdoId(contextPdoId);

        this.confirmCreateContext((success) => {
            if (Config.detailView.AlwaysCloseDetailViewContext) {
                // Wenn wir den DVC schließen, dann wird (höchstwahrscheinlich?) auch der caller disposed,
                // daher reichen wir ihn in diesem Fall nicht weiter.
                caller = null;
            }

            if ((success || this.currentContext() != null) && Config.detailView.AlwaysCloseDetailViewContext) {

                //Es macht nur sinn ein ContextObjekt zu übergeben, wenn ebenfalls die Verbindung beschrieben wurde
                if (String.isNullOrEmpty(contextPdTypeName) || String.isNullOrEmpty(contextRelationName)) {
                    safeContextPdoId = '0';
                }

                if (parseInt(safePdoId) > 0) {
                    // Wenn der DVC geschlossen wird, dann übergeben wir dem System die Kontrolle darüber, was als nächstes passiert
                    // und sagen nur allgemein: navigateAssociation
                    this.navigateAssociation(caller, pdTypeName, safePdoId, safeContextPdoId, contextPdTypeName, contextRelationName, contextParameters, callId, principalFilterMode, filter)
                } else {
                    this.navigateToUnspecifiedObject(caller, pdTypeName, safePdoId, safeContextPdoId, contextPdTypeName, contextRelationName, contextParameters, callId, principalFilterMode, filter);
                }
            }
            else {
                this.openDVCAndPushToStack(caller, pdTypeName, safePdoId, safeContextPdoId, contextPdTypeName, contextRelationName, contextParameters, callId);
            }
        });
    }

    /**
     * Führt die Navigation auf ein spezifiziertes Objekt durch
     * @param caller
     * @param pdTypeName
     * @param pdoId
     * @param contextPdoId
     * @param contextPdTypeName
     * @param contextRelationName
     * @param contextParameters
     * @param callId
     * @param principalFilterMode
     * @param filter
     */
    protected navigateAssociation(caller: any, pdTypeName: string, pdoId: string, contextPdoId: string, contextPdTypeName: string, contextRelationName: string, contextParameters: any,
        callId: string, principalFilterMode: string, filter: string): void {
        const parameter = this.createAssociationNavigationParameter(caller, pdTypeName, pdoId, contextPdoId, contextPdTypeName, contextRelationName, contextParameters, callId, principalFilterMode, filter);
        NavigationHelper.navigateAssociation(pdTypeName, pdoId, parameter);
    }

    /**
     * Erzeugt die optionalen Parameter für den Assoziation-Navigator
     * @param caller
     * @param pdTypeName
     * @param pdoId
     * @param contextPdoId
     * @param contextPdTypeName
     * @param contextRelationName
     * @param contextParameters
     * @param callId
     * @param principalFilterMode
     * @param filter
     */
    protected createAssociationNavigationParameter(caller: any, pdTypeName: string, pdoId: string, contextPdoId: string, contextPdTypeName: string, contextRelationName: string, contextParameters: any,
        callId: string, principalFilterMode: string, filter: string): any {
        return {
            contextSource: 'ALV',
            contextTypeName: contextPdTypeName,
            contextObject: contextPdoId,
            contextRelation: contextRelationName,
            contextId: callId,
            contextParameters: contextParameters,
            principalFilterMode: principalFilterMode,
            filter: filter,
            onNavigationNotPossible: () => {
                // Fallback: Altes Verhalten:
                this.openDVCAndPushToStack(caller, pdTypeName, pdoId, contextPdoId, contextPdTypeName, contextRelationName, contextParameters, callId);
            }
        };
    }

    /**
     * Führt die Navigation auf ein nicht spezifiziertes Objekt (z.B. PDO_ID = -1) durch
     * @param caller
     * @param pdTypeName
     * @param pdoId
     * @param contextPdoId
     * @param contextPdTypeName
     * @param contextRelationName
     * @param contextParameters
     * @param callId
     * @param principalFilterMode
     * @param filter
     */
    protected navigateToUnspecifiedObject(caller: any, pdTypeName: string, pdoId: string, contextPdoId: string, contextPdTypeName: string, contextRelationName: string, contextParameters: any,
        callId: string, principalFilterMode: string, filter: string) {
        Messenger.send(new AssociationNavigationMessage({
            fromPDTypeName: contextPdTypeName,
            fromPdoId: contextPdoId,
            relation: contextRelationName,
            toPDTypeName: pdTypeName,
            toPdoId: pdoId,
        }));
        NavigationHelper.openDetailViewAndConnect('EDIT',
            pdTypeName,
            contextPdoId,
            contextPdTypeName,
            contextRelationName,
            contextParameters);
    }

    public createContextModal(caller: any, pdTypeName: string, pdoId: string | number, contextPdoId?: string | number, contextPdTypeName?: string, contextRelationName?: string, contextParameters?: any,
        callId?: string) {
        const modalDetailViewWindowViewModel = ComponentManager.getViewModel<typeof ModalDetailViewWindowViewModel>('ModalDetailViewWindow');
        const modalDetailViewWindow = ComponentManager.getControl<typeof ModalDetailViewWindow>('ModalDetailViewWindow');

        const vm = new modalDetailViewWindowViewModel({
            parent: this,
            caller: caller,
            pdTypeName: pdTypeName,
            pdoId: pdoId,
            contextPdoId: contextPdoId,
            contextPdTypeName: contextPdTypeName,
            contextRelationName: contextRelationName,
            contextParameters: contextParameters,
            detailViewContextCounter: this.getDetailViewContextCounter() + 1
        });

        const window = new modalDetailViewWindow(vm);
        window.getTuple();
    }

    private openDVCAndPushToStack(caller: DetailViewContextT, pdTypeName: string, pdoId: string, contextPdoId?: string, contextPdTypeName?: string, contextRelationName?: string, contextParameters?: any,
        callId?: string) {
        this.increaseBusyCounter();
        let detailViewContext = new this.DetailViewContext({
            parentLocator: this.locator,
            locator: this.defineComputed(() => {
                return 'dv.' + this.getDetailViewContextCounter();
            }),
            viewModel: this,
            caller: caller,
            pdTypeName: pdTypeName,
            pdoId: pdoId,
            contextPdoId: contextPdoId,
            contextPdTypeName: contextPdTypeName,
            contextRelationName: contextRelationName,
            contextParameters: contextParameters,
            callId: callId,
            action: parseInt(pdoId) > 0 ? 'EDIT' : 'CREATE',
            serializedFilters: this.serializedFilters
        });

        this.defineSubscription(detailViewContext.detailView, (detailView) => {
            if (detailView != null) {
                this.detailViewContextStack.push(detailViewContext);
                NavigationService.getInstance().addInformationToCurrentState({
                    ergName: detailView.ergName,
                    principalName: detailViewContext.genericObject.principalName,
                    pdoId: detailViewContext.genericObject.pdoId
                });
                this.decreaseBusyCounter();
            }
        }, true); // Subscription wird nur einmal pro DVC aufgerufen, da der Kontext nur einmal geladen wird
    }

    private getCurrentContext(): DetailViewContextT {
        if (this.isDisposed) {
            return null;
        }
        return this.currentContext();
    }

    /**
     * Entfernt den aktuellen DetailViewContext
     * @param callback
     */
    public removeCurrentContext(callback: (context: DetailViewContextT) => void) {
        let currentContext = this.currentContext();

        this._detailViewContextCounter--;

        this.deleteDetailViewContexts([currentContext], () => {
            if (callback != null) {
                currentContext = this.getCurrentContext();
                callback(currentContext);
            }
        });
    }

    /**
     * Entfernt einen DetailViewContext
     * @param context
     * @param callback
     */
    public removeContext(context: DetailViewContextT, callback: (context: DetailViewContextT) => void) {
        this.deleteDetailViewContexts([context], () => {
            if (callback != null) {
                const currentContext = this.getCurrentContext();
                callback(currentContext);
            }
        });
    }

    /**
     * Entfernt alle DetailViewContexte
     * @param callback
     */
    public removeContextStack(callback) {
        const stack = this.detailViewContextStack();
        this.deleteDetailViewContexts(stack, callback);
    }

    public deleteDetailViewContexts(contexts: DetailViewContextT[], callback: () => void) {
        contexts = contexts.slice(0).filter((c) => { return !c.isDisposed; }); // lokale Kopie des Arrays anlegen
        if (contexts.length === 0) {
            if (callback != null) {
                callback();
            }

            return;
        }

        DetailViewContextT.delete(contexts).then(() => {
            if (this.isDisposed === false) {
                this.detailViewContextStack.removeAll(contexts);

                for (let j = 0; j < contexts.length; j++) {
                    const context = contexts[j];

                    if (context.isDisposed === false) {
                        context.notifyDetailViewClosed();
                        context.dispose();
                    }
                }

            }

            if (callback != null) {
                callback();
            }
        });
    }

    /**
     * Prüft alle GenericObjects im Stack auf ausstehende Änderungen
     */
    public checkForChangesInStack() {
        const stack = this.detailViewContextStack();

        for (let i = 0; i < stack.length; i++) {
            let context = stack[i];

            if (context.hasPendingModifications()) {
                return true;
            }
        }

        return false;
    }

    /**
     * Wartet bis offene Änderungen im GenericObject verarbeitet wurden 
     * und gibt zurück, ob im Anschluss noch offene Änderungen vorhanden sind
     */
    private waitForCommitingChangesInStack(): JQueryPromise<boolean> {
        const hasChanges = this.checkForChangesInStack();
        const lastContext = _.last(this.detailViewContextStack());
        if (!lastContext?.useShortTransactions())
            return Utilities.resolveImmediately(hasChanges);

        const deferred = $.Deferred();
        Loader.getInstance().show();
        window.setTimeout(() => {
            RADEnvironmentProxy.getInstance().whenAllRequestsAreResponded()
                .then(() => {
                    deferred.resolve(this.checkForChangesInStack());
                }).always(() => {
                    Loader.getInstance().hide();
                });
        }, lastContext.genericObject.delay)

        return deferred.promise();
    }

    /**
     * Erstellt einen neuen NestedDetailViewContext
     * @param nestedContextName
     * @param parent
     */
    public createNestedContext(nestedContextName: string, parent: DetailViewContextT): DetailViewContextT {
        const nestedDetailViewContextDTO = parent._nestedDetailViewContextDTOs._findByName(nestedContextName);

        if (nestedDetailViewContextDTO == null) {
            return null;
        }

        let nestedTypeName;
        if (nestedDetailViewContextDTO.detailView)
            nestedTypeName = nestedDetailViewContextDTO.detailView.typeName;

        return new this.DetailViewContext({
            parentLocator: parent.locator,
            locator: 'dv-nested.[' + nestedContextName + ']',
            viewModel: this,
            parent: parent,
            pdoId: nestedDetailViewContextDTO.subject.pdoId,
            action: parseInt(nestedDetailViewContextDTO.subject.pdoId) > 0 ? 'EDIT' : 'CREATE',
            detailViewContextDTO: nestedDetailViewContextDTO,
            nestedContextName: nestedContextName,
            pdTypeName: nestedTypeName,
            serializedFilters: this.serializedFilters
        });
    }

    protected getDetailViewContextCounter(): number {
        if (Config.detailView.AlwaysCloseDetailViewContext)
            return NavigationService.getInstance().detailViewStack().length - 1;
        else
            return this._detailViewContextCounter++;
    }

    /**
     * Erzeugt das ViewModel für den Seitentitel
     */
    private createPageTitle() {
        const breadCrumbViewModel = ComponentManager.getViewModel<typeof BreadCrumbViewModel>('BreadCrumbViewModel');

        this.pageTitle = new breadCrumbViewModel({
            parentLocator: this.locator,
            locator: 'dv-breadcrumbs',
            elements: this.defineComputed(() => {
                const result = [];

                const stack = Config.detailView.AlwaysCloseDetailViewContext
                    ? NavigationService.getInstance().detailViewStack()
                    : this.createDVStackInformation();

                for (let i = 0; i < stack.length; i++) {
                    const stackItem = stack[i];
                    const hideSuffix = !this.showForeignPrincipalPostfix(stackItem.ergName, stackItem.principalName, stackItem.pdoId);
                    const builder = new LabelViewModelBuilder().withErgName(stackItem.ergName);

                    if (!hideSuffix) {
                        builder.withPostfix()
                            .updateProperties({
                                prefix: ' ',
                                isVisible: this.defineComputed(() => {
                                    let principal = RADApplication.getInstance().principal.name();

                                    return stackItem.principalName !== '' && stackItem.principalName !== principal;
                                }),
                                ergName: 'DETAILVIEW_PRINCIPAL_NAME_SUFFIX'
                            })
                            .withSimpleReplacement('principalName', stackItem.principalName);
                    }

                    result.push(builder.build());
                }

                return result;
            })
        });
    }

    private createDVStackInformation() {
        const result = [];

        const stacks = this.detailViewContextStack();
        for (let i = 0; i < stacks.length; i++) {
            const context = stacks[i];
            if (context.isDisposed)
                return [];

            const detailView = context.detailView();

            const dvInformation = {
                ergName: detailView ? detailView.ergName : '',
                principalName: detailView ? context.genericObject.principalName : '',
                pdoId: context.genericObject.pdoId
            };

            result.push(dvInformation);
        }

        return result;
    }

    private onBeforeNavigateAway(navigateCallback, isNavigatingBack: boolean) {
        this.unregisterMessages();

        const stack = this.detailViewContextStack();
        const isNavBack = isNavigatingBack || NavigationService.getInstance().isNavigatingBack();

        for (let i = 0; i < stack.length; i++) {
            stack[i].setPendingDVC(stack.length > 1);
            if (isNavBack) {
                stack[i]._latestDetailViewAction = 'Cancel';
                stack[i].setReasonForLeaving('IsNavigatingBack');
            }
        }

        this.removeContextStack(navigateCallback);
    }

    private waitForCurrentContextIdle(): JQueryPromise<void> {
        if (!this.isCurrentContextBusy())
            return Utilities.resolveImmediately();

        const deferred = $.Deferred<void>();
        this.subscribeUntil(this.isCurrentContextBusy, busy => {
            if (busy)
                return false;

            deferred.resolve();
            return true;
        });
        return deferred.promise();
    }

    /**
     * Registrierung für Navigations-Message falls der Kontext mit aussstehenden Änderungen verlassen wird
     */
    private registerForNavigationStartedMessage() {
        Messenger.registerForMessageId('NavigationStartedMessage', (message: NavigationStartedMessage) => {
            if (message.isCanceled()) {
                return;
            }

            // Navigation erst einmal unterbinden. Wir rufen später continue auf.
            message.cancel();
            const navigate = () => {
                if (message.isDisposed)
                    return;

                this.onBeforeNavigateAway(() => message.continue(), message.isNavigatingBack);
            };

            this.waitForCommitingChangesInStack().then((hasChanges) => {
                const app = RADApplication.getInstance();
                if (this.isDisposed || app.isForceLogout() || (hasChanges !== true && !this.isCurrentContextBusy())) {
                    navigate();
                    return;
                }

                this.waitForCurrentContextIdle().then(() => {
                    if (!this.isDisposed && this.checkForChangesInStack() && !app.isForceLogout()) {
                        const messageBox = MessageBoxViewModel.createDetailViewDropMessageBox(() => navigate());
                        app.showMessageBox(messageBox);
                    } else {
                        navigate();
                    }
                });
            });
        }, this);
    }

    /**
     * Gibt den Namen des Items zurück, zu dem der Benutzer beim Wiederherstellen der Ansicht gescrollt werden soll.
     */
    public getViewAnchor(): string {
        return this.viewAnchor;
    }

    /**
     * Legt ein Item fest, zu welchem der Benutzer beim Wiederherstellen der Ansicht gescrollt werden soll.
     * @param itemName - Der Name des Items, zu dem gescrollt werden soll. Üblicherweise vom DetailViewItem.
     */
    public setViewAnchor(itemName: string): void {
        this.viewAnchor = itemName;
    }

    /**
     * Deregistriert alle Registrierungen für Messages
     */
    private unregisterMessages(): void {
        Messenger.unregisterForMessageId('NavigationStartedMessage', null, this);
    }

    /**
     * @override
     */
    protected disposeInternal(): void {
        this.unregisterMessages();
        super.disposeInternal();

        RADApplication.getInstance().currentDetailViewContext(null);
    }
}

ComponentManager.setViewModel('DetailViewViewModel', DetailViewViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');